import React, { useState, useRef, useEffect } from "react";
import { Paperclip, Send, X, Mic, User, StopCircle, Pencil } from "lucide-react";
import { convertToWav, formatDuration } from "utils/audioUtils";
import ImageAnnotatorModal from "../../../../components/ImageAnnotatorModal";

const MessageInput = ({
    onSend,
    replyToMessage,
    setReplyToMessage,
    teamMembers,
    isUploading,
    mediaFiles,
    setMediaFiles,
}) => {
    const [text, setText] = useState("");
    // const [mediaFiles, setMediaFiles] = useState([]);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [showMentions, setShowMentions] = useState(false);
    const [mentionQuery, setMentionQuery] = useState("");
    const [mentionPosition, setMentionPosition] = useState(null);
    const [showAnnotator, setShowAnnotator] = useState(false);
    const [annotateIndex, setAnnotateIndex] = useState(null);

    const fileInputRef = useRef(null);
    const textareaRef = useRef(null);
    const timerRef = useRef(null);
    const messageInputRef = useRef(null); // Ref for the container or textarea to position mentions

    // Audio Recording Logic
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: 16000,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });

            const options = {
                mimeType: "audio/webm;codecs=opus",
                audioBitsPerSecond: 128000,
            };

            if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                options.mimeType = "audio/webm";
                if (!MediaRecorder.isTypeSupported(options.mimeType)) {
                    options.mimeType = "";
                }
            }

            const recorder = new MediaRecorder(stream, options);
            const chunks = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            recorder.onstop = async () => {
                try {
                    const mimeType = recorder.mimeType || "audio/webm";
                    const blob = new Blob(chunks, { type: mimeType });
                    const wavBlob = await convertToWav(blob);
                    const audioFile = new File([wavBlob], "recording.wav", {
                        type: "audio/wav",
                    });

                    setMediaFiles((prev) => [...prev, audioFile]);

                    const tracks = stream.getTracks();
                    tracks.forEach((track) => track.stop());
                } catch (error) {
                    console.error("Error converting audio to WAV:", error);
                    const tracks = stream.getTracks();
                    tracks.forEach((track) => track.stop());
                }
            };

            recorder.start();
            setMediaRecorder(recorder);
            setIsRecording(true);
            setRecordingDuration(0);
            timerRef.current = setInterval(() => {
                setRecordingDuration((prev) => prev + 1);
            }, 1000);
        } catch (error) {
            console.error("Error accessing microphone:", error);
            alert("Could not access microphone. Please check permissions.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorder && isRecording) {
            mediaRecorder.stop();
            setIsRecording(false);
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
    };

    // Text Input Logic
    const handleMessageChange = (e) => {
        const value = e.target.value;
        setText(value);
        autoResizeTextarea();

        const lastAtIndex = value.lastIndexOf("@");
        if (
            lastAtIndex !== -1 &&
            (lastAtIndex === 0 || value[lastAtIndex - 1] === " ")
        ) {
            const query = value.substring(lastAtIndex + 1).split(" ")[0];

            // If there's a space after the query, we consider the mention "closed"
            // This prevents the modal from staying open after selecting a mention (which adds a space)
            const isSpaceAfter = value[lastAtIndex + 1 + query.length] === " ";

            if (!isSpaceAfter) {
                setMentionQuery(query);
                // Calculate position
                // This is a simplified positioning. For production, might need a better library or calculation.
                // We'll just show it above the input for now.
                setMentionPosition({ bottom: "100%", left: 0 });
                setShowMentions(true);
            } else {
                setShowMentions(false);
            }
        } else {
            setShowMentions(false);
        }
    };

    const autoResizeTextarea = () => {
        if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height =
                textareaRef.current.scrollHeight + "px";
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey && !showMentions) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleSend = () => {
        if (!text.trim() && mediaFiles.length === 0) return;
        onSend(text); // Files are already in parent state
        setText("");
        // setMediaFiles([]); // Parent handles clearing files after send
        if (fileInputRef.current) fileInputRef.current.value = "";
        if (textareaRef.current) textareaRef.current.style.height = "auto";
    };

    // File Logic
    const handleFileSelection = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            setMediaFiles((prev) => [...prev, ...Array.from(e.target.files)]);
        }
    };

    const removeMediaFile = (index) => {
        setMediaFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // Annotation logic
    const openAnnotator = (index) => {
        setAnnotateIndex(index);
        setShowAnnotator(true);
    };

    const closeAnnotator = () => {
        setShowAnnotator(false);
        setAnnotateIndex(null);
    };

    const handleAnnotatorSave = (blob, { replaceOriginal }) => {
        if (annotateIndex == null) return;
        const original = mediaFiles[annotateIndex];
        const baseName = original?.name ? original.name.replace(/\.[^/.]+$/, "") : "image";
        const annotatedFile = new File([blob], `${baseName}-annotated.png`, {
            type: "image/png",
        });
        setMediaFiles((prev) => {
            const arr = [...prev];
            if (replaceOriginal) {
                arr[annotateIndex] = annotatedFile;
            } else {
                arr.splice(annotateIndex + 1, 0, annotatedFile);
            }
            return arr;
        });
        closeAnnotator();
    };

    // Mentions Logic
    const insertMention = (member) => {
        const lastAtIndex = text.lastIndexOf("@");
        const newText =
            text.substring(0, lastAtIndex) +
            `@${member.first_name} ${member.last_name} ` +
            text.substring(lastAtIndex + 1 + mentionQuery.length);

        setText(newText);
        setShowMentions(false);
        textareaRef.current.focus();
    };

    const filteredMembers = mentionQuery
        ? teamMembers.filter((member) =>
            `${member.first_name} ${member.last_name} `
                .toLowerCase()
                .includes(mentionQuery.toLowerCase())
        )
        : teamMembers;

    return (
        <div className="relative">
            {/* Mentions Dropdown */}
            {showMentions && filteredMembers.length > 0 && (
                <div
                    className="absolute z-10 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto bottom-full mb-2 left-0 min-w-[200px]"
                >
                    {filteredMembers.map((member) => (
                        <div
                            key={member.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer flex items-center"
                            onClick={() => insertMention(member)}
                        >
                            <User size={16} className="mr-2 text-gray-500" />
                            <span>
                                {member.first_name} {member.last_name}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* Reply Banner */}
            {replyToMessage && (
                <div className="px-4 py-2 bg-gray-100 border-b flex items-center justify-between">
                    <div>
                        <div className="text-xs text-gray-500">
                            Replying to {replyToMessage.senderName}
                        </div>
                        <div className="text-sm text-gray-800 truncate max-w-xs">
                            {replyToMessage.text ||
                                (replyToMessage.has_media ? "Media message" : "")}
                        </div>
                    </div>
                    <button
                        className="ml-4 text-gray-500 hover:text-red-500"
                        onClick={() => setReplyToMessage(null)}
                    >
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Media Files Preview */}
            {mediaFiles.length > 0 && (
                <div className="p-2 bg-gray-100 border-t flex flex-wrap gap-2 items-center">
                    {mediaFiles.map((file, index) => (
                        <div
                            key={index}
                            className="flex items-center text-sm text-gray-600 bg-white rounded px-2 py-1"
                        >
                            <Paperclip size={16} className="mr-2" />
                            <span className="truncate max-w-[150px]">
                                {file.name} ({Math.round(file.size / 1024)} KB)
                            </span>
                            {file.type && file.type.startsWith("image/") && (
                                <button
                                    onClick={() => openAnnotator(index)}
                                    className="ml-2 text-gray-500 hover:text-indigo-600"
                                    title="Annotate image"
                                    disabled={isUploading || isRecording}
                                >
                                    <Pencil size={16} />
                                </button>
                            )}
                            <button
                                onClick={() => removeMediaFile(index)}
                                className="ml-2 text-gray-500 hover:text-red-500"
                            >
                                <X size={16} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Input Area */}
            <div className="p-3 bg-white border-t flex items-center gap-2">
                <button
                    onClick={() => fileInputRef.current.click()}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
                    disabled={isUploading || isRecording}
                >
                    <Paperclip size={20} />
                </button>
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelection}
                    accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,.ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                    multiple
                    className="hidden"
                />

                {isRecording ? (
                    <div className="flex-1 flex items-center gap-3 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-red-600 font-medium text-sm">
                            Recording {formatDuration(recordingDuration)}
                        </span>
                        <div className="flex-1" />
                        <button
                            onClick={stopRecording}
                            className="p-1 text-red-600 hover:bg-red-100 rounded-full transition-colors"
                        >
                            <StopCircle size={20} />
                        </button>
                    </div>
                ) : (
                    <>
                        <textarea
                            ref={textareaRef}
                            className="flex-1 border rounded-lg px-3 py-2 resize-none overflow-hidden min-h-[40px] max-h-[120px]"
                            placeholder={
                                isUploading
                                    ? "Uploading media..."
                                    : "Type a message... (Shift+Enter for new line)"
                            }
                            value={text}
                            onChange={handleMessageChange}
                            onKeyDown={handleKeyDown}
                            disabled={isUploading}
                            rows={1}
                            style={{ lineHeight: "1.5" }}
                        />
                        <button
                            onClick={startRecording}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
                            disabled={isUploading}
                            title="Record audio"
                        >
                            <Mic size={20} />
                        </button>
                    </>
                )}

                <button
                    onClick={handleSend}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
                    disabled={
                        isUploading ||
                        isRecording ||
                        (!text.trim() && mediaFiles.length === 0)
                    }
                >
                    <Send size={20} />
                </button>
            </div>
            {showAnnotator && annotateIndex != null && mediaFiles[annotateIndex] && (
                <ImageAnnotatorModal
                    imageFile={mediaFiles[annotateIndex]}
                    onClose={closeAnnotator}
                    onSave={handleAnnotatorSave}
                />
            )}
        </div>
    );
};

export default MessageInput;
